import type { LucideIcon } from 'lucide-react'
import type { FC, ReactNode } from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export interface ChatInputModeButtonProps {
  icon: LucideIcon
  label: string
  tooltipText: string
  isSelected: boolean
  isDisabled: boolean
  onClick: () => void
  className?: string
  selectedClassName?: string
}

/**
 * Individual mode button for the chat input mode switch.
 */
export const ChatInputModeButton: FC<ChatInputModeButtonProps> = ({
  icon: Icon,
  label,
  tooltipText,
  isSelected,
  isDisabled,
  onClick,
  className,
  selectedClassName,
}): ReactNode => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        role="tab"
        disabled={isDisabled}
        onClick={onClick}
        aria-selected={isSelected}
        aria-label={`Send as ${label.toLowerCase()}`}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium',
          'transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isSelected && selectedClassName,
          !isSelected && 'text-muted-foreground hover:text-foreground',
          className
        )}
      >
        <Icon className="size-3.5" />
        <span>{label}</span>
      </button>
    </TooltipTrigger>
    <TooltipContent side="top">{tooltipText}</TooltipContent>
  </Tooltip>
)

ChatInputModeButton.displayName = 'ChatInputModeButton'
